/**
 * TECHNICIAN BACKGROUND vs OBSERVED PROFICIENCY — the words for both, kept apart.
 *
 *   BACKGROUND  what the technician brings into OptiBay ("Veteran technician ·
 *               18 years professional experience"). Stated by a person; stored
 *               in technician_trade_background; DISPLAY CONTEXT ONLY — nothing
 *               scores, ranks or assigns from it.
 *
 *   EVIDENCE    what OptiBay has learned from their work, per category — CORE-E
 *               technician_category_authority. Three honest states:
 *                 no_history   "Not enough OptiBay history yet"
 *                 building     "Building evidence" (observed work, not yet
 *                              outweighing the opening estimate)
 *                 established  observed work now outweighs the opening estimate
 *               The crossing point is CORE-E's own (evidenceHasSupersededPrior,
 *               with the row's prior strength) — not a new threshold.
 *
 * "No OptiBay evidence" is never rendered as 0 / Novice / Beginner, and a
 * missing background is "not provided", never "New to the trade".
 */
import { evidenceHasSupersededPrior } from '@/hooks/passport/useCategoryAuthority';

export type ExperienceLevel = 'NEW_TO_TRADE' | 'DEVELOPING' | 'EXPERIENCED' | 'VETERAN';

export const EXPERIENCE_LEVELS: ReadonlyArray<{ value: ExperienceLevel; label: string; hint: string }> = Object.freeze([
  { value: 'NEW_TO_TRADE', label: 'New to the trade', hint: 'Just starting out as a technician' },
  { value: 'DEVELOPING', label: 'Developing technician', hint: 'Some professional experience, still building skills' },
  { value: 'EXPERIENCED', label: 'Experienced technician', hint: 'Works independently on most jobs' },
  { value: 'VETERAN', label: 'Veteran technician', hint: 'Many years in the trade' },
]);

const LABEL: Record<ExperienceLevel, string> = Object.fromEntries(
  EXPERIENCE_LEVELS.map((l) => [l.value, l.label]),
) as Record<ExperienceLevel, string>;

export function isExperienceLevel(v: unknown): v is ExperienceLevel {
  return typeof v === 'string' && v in LABEL;
}

export interface TradeBackgroundRow {
  experience_level: string | null;
  years_professional: number | null;
  source?: string | null;
}

export interface TradeBackgroundView {
  /** False when nothing was provided (legacy, skipped, never asked). */
  provided: boolean;
  /** "Veteran technician" / "Background not provided". */
  headline: string;
  /** "18 years professional experience", or null. */
  yearsLine: string | null;
  /** "Self-reported" / "Entered by the shop", or null. */
  sourceLine: string | null;
}

export function yearsLabel(years: number | null | undefined): string | null {
  if (years == null || !Number.isFinite(years)) return null;
  if (years === 0) return 'Less than a year of professional experience';
  return `${years} ${years === 1 ? 'year' : 'years'} professional experience`;
}

export function describeTradeBackground(row: TradeBackgroundRow | null | undefined): TradeBackgroundView {
  const level = isExperienceLevel(row?.experience_level) ? row!.experience_level as ExperienceLevel : null;
  const yearsLine = yearsLabel(row?.years_professional ?? null);
  if (!level && !yearsLine) {
    return { provided: false, headline: 'Background not provided', yearsLine: null, sourceLine: null };
  }
  const sourceLine = row?.source === 'TECHNICIAN_SELF_REPORTED'
    ? 'Self-reported'
    : row?.source === 'MANAGER_ASSERTED' ? 'Entered by the shop' : null;
  return {
    provided: true,
    headline: level ? LABEL[level] : 'Technician',
    yearsLine,
    sourceLine,
  };
}

/** "Veteran technician · 18 years" — one line for compact spots. */
export function tradeBackgroundShortLine(row: TradeBackgroundRow | null | undefined): string {
  const v = describeTradeBackground(row);
  if (!v.provided) return v.headline;
  const y = row?.years_professional;
  return y != null && y > 0 ? `${v.headline} · ${y} ${y === 1 ? 'year' : 'years'}` : v.headline;
}

// ── Observed proficiency, per category ─────────────────────────────────────
export type EvidenceState = 'no_history' | 'building' | 'established';

export interface AuthorityEvidenceInput {
  categoryKey: string;
  score: number | null;
  baselineScore: number | null;
  baselineClass: string | null;
  baselinePriorStrength: number | null;
  evidenceCount: number | null;
}

export interface CategoryEvidenceView {
  categoryKey: string;
  state: EvidenceState;
  /** "Established" / "Building evidence" / "Not enough OptiBay history yet". */
  label: string;
  /** "82%" — only when established (an observed score), never invented. */
  scoreLabel: string | null;
  /** "Based on 27 observed jobs" / "OptiBay has observed 2 jobs" / null. */
  basis: string | null;
  /** The shop's opening estimate, labelled as such — not a measurement. */
  openingEstimate: string | null;
}

const jobs = (n: number) => `${n} ${n === 1 ? 'job' : 'jobs'}`;

export function categoryEvidenceView(row: AuthorityEvidenceInput): CategoryEvidenceView {
  const n = Math.max(0, Number(row.evidenceCount ?? 0) || 0);
  const opening = row.baselineScore != null && Number.isFinite(row.baselineScore)
    ? `Opening estimate from ${row.baselineClass === 'PASSPORT_CARRIED' ? 'their Passport' : 'the shop'}: ${Math.round(row.baselineScore)}%`
    : null;
  if (n === 0) {
    return { categoryKey: row.categoryKey, state: 'no_history', label: 'Not enough OptiBay history yet', scoreLabel: null, basis: null, openingEstimate: opening };
  }
  // With an opening estimate, observed work is established once it outweighs
  // that estimate (CORE-E's crossing point, at the row's own prior strength).
  // With none, CORE-E only produces a score once work is demonstrated — the
  // score itself is the evidence.
  const established = row.score != null && (
    row.baselineScore == null || evidenceHasSupersededPrior(n, row.baselinePriorStrength ?? undefined));
  if (!established) {
    return { categoryKey: row.categoryKey, state: 'building', label: 'Building evidence', scoreLabel: null, basis: `OptiBay has observed ${jobs(n)}`, openingEstimate: opening };
  }
  return {
    categoryKey: row.categoryKey,
    state: 'established',
    label: 'Established',
    scoreLabel: `${Math.round(row.score as number)}%`,
    basis: `Based on ${n} observed ${n === 1 ? 'job' : 'jobs'}`,
    openingEstimate: null,
  };
}

/** "Since Mar 2026 · 14 completed jobs" — only facts that are stored. */
export function optibayHistoryLine(input: { since?: string | null; completedJobs?: number | null }): string | null {
  const parts: string[] = [];
  if (input.since) {
    const d = new Date(input.since);
    if (Number.isFinite(d.getTime())) {
      parts.push(`On OptiBay since ${d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' })}`);
    }
  }
  if (input.completedJobs != null && Number.isFinite(input.completedJobs)) {
    parts.push(`${input.completedJobs} completed ${input.completedJobs === 1 ? 'job' : 'jobs'}`);
  }
  return parts.length ? parts.join(' · ') : null;
}
